import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  Typography
} from '@mui/material';
import CalculateIcon from '@mui/icons-material/Calculate';
import RefreshIcon from '@mui/icons-material/Refresh';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import StandardListView from '../StandardComponents/StandardListView/StandardListView';
import useD8ListLayout from '../../hooks/useD8ListLayout';
import { calculateEmailForecast } from '../../Api/SystemEmailLogs';

const BUCKET_KEYS = { NEXT_24H: 'next_24h', NEXT_48H: 'next_48h', WITHIN_7D: 'within_7d' };

const BUCKET_LABELS = {
  [BUCKET_KEYS.NEXT_24H]: 'Next 24 hours',
  [BUCKET_KEYS.NEXT_48H]: 'Next 24-48 hours',
  [BUCKET_KEYS.WITHIN_7D]: '48h - 7 days'
};

// Not-implemented rules are a planned/pending state, not an error, so they get
// an amber "pending" treatment rather than a red one - red is reserved for
// rules that are actively broken, which the forecast never reports here.
const RULE_STATUS_META = {
  active: { label: 'Active', color: '#047857', bg: '#ECFDF5', border: '#A7F3D0', icon: CheckCircleIcon },
  disabled: { label: 'Disabled', color: '#6B7280', bg: '#F3F4F6', border: '#E5E7EB', icon: PauseCircleOutlineIcon },
  not_implemented: { label: 'Not implemented', color: '#B45309', bg: '#FFFBEB', border: '#FDE68A', icon: HourglassEmptyIcon }
};

const getRuleStatusMeta = (status) =>
  RULE_STATUS_META[status] || { label: status, color: '#6B7280', bg: '#F3F4F6', border: '#E5E7EB', icon: InfoOutlinedIcon };

const StatusPill = ({ status }) => {
  const meta = getRuleStatusMeta(status);
  const StatusIcon = meta.icon;
  return (
    <Chip
      size="small"
      icon={<StatusIcon sx={{ fontSize: 14, color: `${meta.color} !important` }} />}
      label={meta.label}
      sx={{
        backgroundColor: meta.bg,
        color: meta.color,
        border: `1px solid ${meta.border}`,
        fontWeight: 600,
        fontSize: '0.7rem'
      }}
    />
  );
};

const formatDateTime = (value) => {
  if (!value) return '—';
  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit'
    });
  } catch {
    return value;
  }
};

const SummaryCard = ({ label, count, tone }) => (
  <Box
    sx={{
      flex: 1,
      p: 2.5,
      borderRadius: '10px',
      border: '1px solid #E5E7EB',
      backgroundColor: tone || '#F9FAFB',
      minWidth: 160
    }}
  >
    <Typography variant="caption" sx={{ color: '#6B7280', fontWeight: 600, textTransform: 'uppercase' }}>
      {label}
    </Typography>
    <Typography variant="h4" sx={{ fontWeight: 700, color: '#111827', mt: 0.5 }}>
      {count}
    </Typography>
  </Box>
);

// Each rule gets one card: its label + status pill on top, and a stat strip
// on the bottom. "action" rules (already-sent notifications) show sent/last-
// fired counts; forecast rules show the 24h/48h/7d/total breakdown, so the
// numbers read as labeled metrics instead of a dense "0 / 0 / 0" string.
const RuleSummaryCard = ({ rule }) => {
  const isAction = rule.category === 'action';
  const stats = isAction
    ? [
        { label: 'Sent (30d)', value: rule.sent_last_30d || 0 },
        { label: 'Last Fired', value: formatDateTime(rule.last_fired_at), small: true }
      ]
    : [
        { label: '24h', value: rule.next_24h },
        { label: '48h', value: rule.next_48h },
        { label: '7d', value: rule.within_7d },
        { label: 'Total', value: rule.total, emphasize: true }
      ];
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: '10px',
        border: '1px solid #E5E7EB',
        backgroundColor: '#FFFFFF',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.25,
        height: '100%'
      }}
    >
      <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#111827', lineHeight: 1.35 }}>
          {rule.rule_label}
        </Typography>
        <StatusPill status={rule.rule_status} />
      </Stack>

      <Stack direction="row" sx={{ mt: 'auto', pt: 1.25, borderTop: '1px solid #F3F4F6' }}>
        {stats.map((s, idx) => (
          <Box
            key={s.label}
            sx={{ flex: 1, textAlign: 'center', borderLeft: idx > 0 ? '1px solid #F3F4F6' : 'none' }}
          >
            <Typography sx={{ fontSize: '0.65rem', color: '#9CA3AF', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em' }}>
              {s.label}
            </Typography>
            <Typography sx={{ fontSize: s.small ? '0.75rem' : '0.95rem', fontWeight: 700, color: s.emphasize ? '#2563EB' : '#111827', mt: 0.25 }}>
              {s.value}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Box>
  );
};

const QueuedEmailForecast = () => {
  // This component is already rendered inside EmailNotificationsPage's own
  // viewport-height flex shell (pageShellSx + listFlexSx), so it must NOT
  // apply a second fixed-height/overflow-hidden shell on top - that double
  // counts APP_CONTENT_TOP_OFFSET and clips the table with no way to scroll
  // to it once the banner/cards/tabs above take up real space. Instead this
  // wrapper just fills the height its parent already gives it and scrolls
  // internally when its own content (banner + cards + table) is taller than
  // that.
  const { listViewProps } = useD8ListLayout();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [orgScope, setOrgScope] = useState('current');
  const [activeBucket, setActiveBucket] = useState(BUCKET_KEYS.NEXT_24H);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const organizationId = localStorage.getItem('organizationId') || '';

  const handleCalculate = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await calculateEmailForecast({
        organizationId: orgScope === 'all' ? null : organizationId,
        horizonDays: 7
      });
      setResult(res.data);
      setPage(0);
    } catch (err) {
      console.error('Failed to calculate email forecast:', err);
      setError('Failed to calculate the email forecast');
    } finally {
      setLoading(false);
    }
  };

  const bucketRows = useMemo(() => {
    if (!result?.buckets) return [];
    return result.buckets[activeBucket] || [];
  }, [result, activeBucket]);

  const totals = useMemo(() => {
    if (!result?.buckets) return { next_24h: 0, next_48h: 0, within_7d: 0 };
    return {
      next_24h: (result.buckets[BUCKET_KEYS.NEXT_24H] || []).length,
      next_48h: (result.buckets[BUCKET_KEYS.NEXT_48H] || []).length,
      within_7d: (result.buckets[BUCKET_KEYS.WITHIN_7D] || []).length
    };
  }, [result]);

  const columns = [
    {
      label: 'Predicted At',
      field: 'predicted_at',
      render: (row) => formatDateTime(row.predicted_at)
    },
    { label: 'Rule', field: 'rule_label' },
    {
      label: 'Rule Status',
      field: 'rule_status',
      render: (row) => <StatusPill status={row.rule_status} />
    },
    { label: 'Organization', field: 'organization_name' },
    { label: 'Recipient', field: 'recipient_name' },
    { label: 'Email', field: 'recipient_email' },
    { label: 'Context', field: 'context_type' },
    { label: 'Reason', field: 'reason' }
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, overflowY: 'auto' }}>
      <Box sx={{ mb: 2, flexShrink: 0 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.25,
            p: 1.75,
            mb: 1.5,
            borderRadius: '10px',
            backgroundColor: '#F0F9FF',
            border: '1px solid #BAE6FD'
          }}
        >
          <InfoOutlinedIcon sx={{ color: '#0284C7', fontSize: 20, mt: '2px', flexShrink: 0 }} />
          <Typography variant="body2" sx={{ color: '#0C4A6E' }}>
            There is no persistent send queue. This recomputes which scheduled emails{' '}
            <Box component="span" sx={{ fontWeight: 700 }}>would</Box> fire in the next 24h / 48h / 7 days
            by re-evaluating each rule against live data.
          </Typography>
        </Box>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} justifyContent="space-between">
          <Typography variant="body2" sx={{ color: '#374151', fontWeight: 500 }}>
            {result
              ? `Calculated ${formatDateTime(result.calculated_at)} · horizon ${result.horizon_days} day(s)`
              : 'Click Calculate to run a fresh projection.'}
          </Typography>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexShrink: 0 }}>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Organization</InputLabel>
              <Select label="Organization" value={orgScope} onChange={(e) => setOrgScope(e.target.value)}>
                <MenuItem value="current">Current Organization</MenuItem>
                <MenuItem value="all">All Organizations</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="contained"
              startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <CalculateIcon />}
              onClick={handleCalculate}
              disabled={loading}
            >
              {loading ? 'Calculating...' : result ? 'Recalculate' : 'Calculate Forecast'}
            </Button>
          </Stack>
        </Stack>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }}>{error}</Alert>}

      {!result && !loading && !error && (
        <Alert severity="info" sx={{ flexShrink: 0 }}>
          No forecast calculated yet. Click "Calculate Forecast" above to project upcoming sends.
        </Alert>
      )}

      {result && (
        <>
          <Stack direction="row" spacing={2} sx={{ mb: 2, flexShrink: 0, flexWrap: 'wrap' }}>
            <SummaryCard label="Next 24 hours" count={totals.next_24h} tone="#EFF6FF" />
            <SummaryCard label="Next 24-48 hours" count={totals.next_48h} tone="#F0FDF4" />
            <SummaryCard label="48h - 7 days" count={totals.within_7d} tone="#FEF3F2" />
          </Stack>

          {result.summary?.length > 0 && (
            <Box sx={{ mb: 2, flexShrink: 0 }}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' },
                  gap: 1.5
                }}
              >
                {result.summary.map((rule) => (
                  <RuleSummaryCard key={rule.rule_key} rule={rule} />
                ))}
              </Box>
            </Box>
          )}

          {result.unknowns?.length > 0 && (
            <Stack spacing={1} sx={{ mb: 2, flexShrink: 0 }}>
              {result.unknowns.map((u, idx) => (
                <Alert key={idx} severity="warning">
                  <strong>{u.source}:</strong> {u.note}
                </Alert>
              ))}
            </Stack>
          )}

          <Tabs
            value={activeBucket}
            onChange={(_, v) => { setActiveBucket(v); setPage(0); }}
            sx={{ mb: 1, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}
          >
            {Object.values(BUCKET_KEYS).map((key) => (
              <Tab key={key} value={key} label={`${BUCKET_LABELS[key]} (${(result.buckets[key] || []).length})`} />
            ))}
          </Tabs>

          <Box sx={{ flexShrink: 0 }}>
            <StandardListView
              {...listViewProps}
              fillAvailableHeight={false}
              embeddedTableMaxHeight={560}
              columns={columns}
              rows={bucketRows}
              title={BUCKET_LABELS[activeBucket]}
              rightHeaderExtras={
                <Button size="small" startIcon={<RefreshIcon />} onClick={handleCalculate} disabled={loading}>
                  Refresh
                </Button>
              }
              showMoreIcon={false}
              readOnly
              totalRows={bucketRows.length}
              page={page}
              rowsPerPage={rowsPerPage}
              onChangePage={(e, newPage) => setPage(newPage)}
              onChangeRowsPerPage={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            />
          </Box>
        </>
      )}
    </Box>
  );
};

export default QueuedEmailForecast;
